import { useCallback, useEffect, useRef, useState } from 'react';

// Drag-to-resize state for the AI assistant drawer. The width is persisted to
// localStorage so a user's chosen size survives reloads. The panel is anchored to
// the right edge, so the dragged width is derived from the distance between the
// cursor and the right edge of the viewport.
//
// The maximum is a share of the viewport rather than a fixed pixel value, so the
// app content is left the remaining 60% on every screen size.
const MIN_WIDTH = 320;
const MAX_WIDTH_RATIO = 0.4;
const DEFAULT_WIDTH = 400;
const STORAGE_KEY = 'ai-assistant-panel-width';

function maxWidth(): number {
  // No window (SSR / non-DOM): fall back to the default so clamp() is a no-op.
  if (typeof window === 'undefined') {
    return DEFAULT_WIDTH;
  }
  return Math.round(window.innerWidth * MAX_WIDTH_RATIO);
}

function clamp(value: number): number {
  // Math.min last, so the 40% cap wins over MIN_WIDTH on a narrow viewport.
  return Math.min(Math.max(value, MIN_WIDTH), maxWidth());
}

function readStoredWidth(): number {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? parseInt(raw, 10) : NaN;
    return clamp(Number.isFinite(parsed) ? parsed : DEFAULT_WIDTH);
  } catch {
    return clamp(DEFAULT_WIDTH);
  }
}

export function usePanelWidth() {
  const [width, setWidth] = useState<number>(readStoredWidth);
  const [isResizing, setIsResizing] = useState(false);
  const cleanupRef = useRef<() => void>(() => undefined);

  const startResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);

    const onMouseMove = (moveEvent: MouseEvent) => {
      setWidth(clamp(window.innerWidth - moveEvent.clientX));
    };

    const onMouseUp = () => {
      cleanup();
      setIsResizing(false);
      setWidth((current) => {
        try {
          localStorage.setItem(STORAGE_KEY, String(current));
        } catch {
          // Ignore storage failures (e.g. private mode); width still applies in-session.
        }
        return current;
      });
    };

    const cleanup = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    document.body.style.userSelect = 'none';
    cleanupRef.current = cleanup;
  }, []);

  // Ensure listeners and the body style are removed if the panel unmounts mid-drag.
  useEffect(() => () => cleanupRef.current(), []);

  // clamp() is viewport-relative, so a stored or dragged width can exceed a window
  // that is later narrowed. The shell reserves margin-right equal to this width, so
  // leaving it stale squeezes the app content to nothing.
  useEffect(() => {
    const onWindowResize = () => setWidth((current) => clamp(current));
    window.addEventListener('resize', onWindowResize);
    return () => window.removeEventListener('resize', onWindowResize);
  }, []);

  return { width, isResizing, startResize };
}
